import { colorPrimary } from '@atlas-frontend/design-tokens';
import {
  FC,
  PropsWithChildren,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Header, RowData, Table } from '@tanstack/react-table';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import styled from 'styled-components';
import { useQueryParams } from '../../queryParams/useQueryParams';
import { HeaderCellTemplate } from '../common/HeaderCellTemplate';

export interface ColumnOrderContextProps extends PropsWithChildren {
  table: Table<RowData>;
  columnOrder: string[];
  defaultColumnOrder: string[];
  onColumnOrderChange: (columnOrder: string[]) => void;
  wrapperRef: RefObject<HTMLDivElement | null>;
  persistColumnStyle: boolean;
}

export const ColumnOrdering: FC<ColumnOrderContextProps> = (props) => {
  const {
    table,
    columnOrder,
    defaultColumnOrder,
    onColumnOrderChange,
    wrapperRef,
    children,
    persistColumnStyle,
  } = props;
  const [, setQueryParams] = useQueryParams();

  useEffect(() => {
    if (!persistColumnStyle) {
      return;
    }
    setQueryParams(
      defaultColumnOrder.toString() !== columnOrder.toString()
        ? { columnOrder: columnOrder.join(',') }
        : { columnOrder: undefined },
    );
  }, [columnOrder, persistColumnStyle]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [caretPosition, setCaretPosition] = useState<number | null>(null);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id.toString());
  }, []);

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!wrapperRef.current || !over) {
        return;
      }

      if (active.id === over.id) {
        setCaretPosition(null);
        return;
      }

      const side =
        over.data.current?.sortable.index < active.data.current?.sortable.index
          ? 'left'
          : 'right';

      setCaretPosition(
        over.rect[side] - wrapperRef.current.getBoundingClientRect().left,
      );
    },
    [wrapperRef],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      setActiveDragId(null);
      setCaretPosition(null);

      if (over && active.id !== over.id) {
        const oldIndex = columnOrder.indexOf(active.id.toString());
        const newIndex = columnOrder.indexOf(over.id.toString());
        onColumnOrderChange(arrayMove(columnOrder, oldIndex, newIndex));
      }
    },
    [columnOrder, onColumnOrderChange],
  );

  const headerGroups = table.getHeaderGroups();

  const headersById = useMemo(
    () =>
      headerGroups.reduce<Record<string, Header<any, unknown>>>(
        (acc, headerGroup) => {
          headerGroup.headers.forEach((header) => {
            acc[header.id] = header;
          });
          return acc;
        },
        {},
      ),
    [headerGroups],
  );

  const draggedHeader = useMemo(
    () => (activeDragId ? headersById[activeDragId] : null),
    [activeDragId, headersById],
  );

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      collisionDetection={closestCenter}
    >
      <SortableContext items={columnOrder}>{children}</SortableContext>
      {draggedHeader && (
        <DragOverlay>
          <HeaderCellTemplate
            header={draggedHeader}
            disabled
            appearsActive
            as="div"
          />
        </DragOverlay>
      )}
      {caretPosition !== null && (
        <Caret style={{ transform: `translateX(${caretPosition}px)` }} />
      )}
    </DndContext>
  );
};

const Caret = styled.div`
  position: absolute;
  z-index: 3;
  top: 0;
  left: 0;
  right: 0;
  width: 2px;
  height: 100%;
  margin-left: -1px;
  background-color: ${colorPrimary};
  pointer-events: none;

  &:before {
    content: '';
    position: absolute;
    top: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: 8px;
    height: 8px;
    border-radius: 100%;
    border: 2px solid ${colorPrimary};
    background-color: white;
  }
`;
